import React from 'react'
import { qualityLabelPhotos } from '../utils/qualityLabelPhotos'
import { NavLink } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TablePagination,
  useMediaQuery,
} from '@mui/material'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { getQualityControls, getQualityControl, updateQualityControlStatus, getFileDataUrl } from '../api/suiteApi'
import { AnimatedSection, AnimatedTableRow } from '../components/animations/MotionContainers'
import { useSuiteSession } from '../context/SuiteSessionContext'
import { APPLICATION_CODES } from '../data'
import ClasolLogo from '../media/logo_clasol-trasparente 1.png'
import { getApplicationAccess, getQualityCapabilities, getUsuarioFromSession } from '../utils/permisos'
import { getWarehouseLabel } from '../utils/warehouses'
import { buildQualityPdfHtml } from '../utils/qualityPdf'
import { SignatureDialog } from '../components/quality/SignatureDialog'
import { useUnsavedChanges } from '../context/UnsavedChangesContext'
import { qualityControlChanges, qualityEventLabel } from '../utils/qualityControlChanges'

const statusClass = {
  Borrador: 'admin-status-warning',
  Completado: 'admin-status-success',
  'Pendiente revision': 'admin-status-warning',
  'Con incidencia': 'admin-status-error',
  Cancelado: 'admin-status-error',
}

const backendStatusLabel = {
  DRAFT: 'Borrador',
  PENDING_REVIEW: 'Pendiente revision',
  COMPLETED: 'Completado',
  WITH_INCIDENT: 'Con incidencia',
  CANCELLED: 'Cancelado',
}

const momentLabel = {
  START: 'Inicio',
  DURING: 'Durante',
  CHANGES: 'Cambios',
  END: 'Final',
}

const resultLabel = {
  OK: 'OK',
  REVIEW: 'Revision',
  INCIDENT: 'Incidencia',
}

const certifiedProductLabel = {
  no: 'No',
  si: 'Si',
  igp: 'IGP',
  global: 'GLOBAL',
}

const samplingRules = [
  { format: 'Mallas', rowSpan: 3, weight: 'Menor o igual 2kg', pallets: 'cualquiera', samples: '3 bolsas/palet' },
  { weight: 'Mayor a 2 kg', pallets: 'Hasta 5', samples: '3 bolsas/palet' },
  { weight: '', pallets: 'Mas de 5', samples: '2 bolsas/palet' },
  { format: 'Granel y encajado', rowSpan: 3, weight: 'Menor o igual a 2.3kg', pallets: 'Cualquiera', samples: '2 cajas/palet' },
  { weight: 'Mayor a 2.3 kg', pallets: 'Hasta 3', samples: '1 caja/palet' },
  { weight: '', pallets: 'Mas de 3', samples: '1 cajas/2 palet' },
]

const responseList = (response) => (
  Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : []
)

const apiMessage = (error, fallback) => (
  error?.payload?.message ||
  error?.message ||
  fallback
)

const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const normalizeControl = (control) => ({
  backendId: control.id,
  id: control.controlNo || control.id,
  order: control.orderNo || control.sourceDocumentNo || '-',
  line: String(control.lineNo || control.sourceLineNo || ''),
  warehouse: control.warehouseCode ? getWarehouseLabel(control.warehouseCode, control.warehouseName) : (control.warehouseName || '-'),
  date: formatDateTime(control.controlDate || control.createdAt),
  client: control.customerName || '-',
  variety: control.varietyName || '-',
  moment: momentLabel[control.controlMoment] || control.controlMoment || '-',
  result: resultLabel[control.result] || control.result || '-',
  status: control.status?.label || backendStatusLabel[control.statusCode] || control.statusCode || '-',
  responsible: control.responsibleUser?.displayName || control.signatures?.[0]?.signedByName || control.createdBy?.displayName || '-',
  reviewer: control.reviewedBy?.displayName || '-',
  control,
})

const okNoOkLabel = (value) => {
  const normalized = String(value || '').toLowerCase()
  return normalized === 'ok' ? 'OK' : normalized === 'no-ok' ? 'No OK' : value || '-'
}

const checkValue = (control, code, fallback) => (
  (control?.checks || []).find((item) => item.code === code)?.value || fallback
)

const requiresSystemMm = (customerCode) => (
  ['2292', '2310', '2309'].includes(String(customerCode || '').trim())
)

const generateControlPdf = (row, openedWindow) => {
  const control = row.control || row
  const detail = control.detail || {}
  const responsibleSignature = (control.signatures || []).find((signature) => signature.signatureType === 'RESPONSIBLE')
  const reviewerSignature = (control.signatures || []).filter((signature) => signature.signatureType === 'REVIEWER').slice(-1)[0]
  const signatureDataUrl = responsibleSignature?.signatureData?.dataUrl || ''
  const printWindow = openedWindow || window.open('', '_blank')
  if (!printWindow) return false

  const infoRows = [
    ['Parte', control.controlNo || row.id],
    ['Pedido', control.orderNo || row.order],
    ['Linea', control.lineNo || row.line],
    ['Almacen', control.warehouseCode ? getWarehouseLabel(control.warehouseCode, control.warehouseName) : (control.warehouseName || row.warehouse)],
    ['Cliente', control.customerName || row.client],
    ['Destino', control.destinationName],
    ['Fecha control', formatDateTime(control.controlDate || control.createdAt) || row.date],
    ['Fecha salida', control.plannedDepartureDate],
    ['Confeccion', control.confectionName],
    ['Variedad', control.varietyName || row.variety],
    ['Marca', control.brand],
  ]

  const qualityRows = [
    ['Momento del control', momentLabel[control.controlMoment] || control.controlMoment || row.moment],
    ['Resultado', resultLabel[control.result] || control.result || row.result],
    ['Estado', control.status?.label || backendStatusLabel[control.statusCode] || row.status],
    ['Producto certificado', certifiedProductLabel[detail.certifiedProduct] || detail.certifiedProduct],
    ['Tipo de palet', detail.palletType],
    ['N de bultos', detail.packages],
    ['Tipo de caja', detail.boxType],
    ['Calibre', detail.caliber],
    ['Calibre real', detail.realCaliber],
    ['Banda', detail.band],
    ['Variedad real', detail.realVariety],
    ['Peso min. palet', detail.minWeight],
    ['Estado envase', okNoOkLabel(checkValue(control, 'PACKAGING_STATUS', detail.packagingStatus))],
    ['Etiqueta malla', okNoOkLabel(checkValue(control, 'MESH_LABEL_OK', '-'))],
    ['Etiqueta caja', okNoOkLabel(checkValue(control, 'BOX_LABEL_OK', '-'))],
    ['N palets pedido', detail.orderedPallets],
    ['Total ampliacion', detail.totalExtension],
  ]

  const html = buildQualityPdfHtml({
    title: 'Control calidad produccion - ' + (control.controlNo || row.id),
    subtitle: (control.controlNo || row.id) + ' - ' + (control.orderNo || row.order) + ' - Linea ' + (control.lineNo || row.line),
    logo: ClasolLogo, infoRows, qualityRows, samplingRules,
    systemMm: requiresSystemMm(control.customerCode),
    photos: qualityLabelPhotos(control.photos),
    notes: [['Observ. de linea', detail.lineNotes], ['Observaciones de calidad', detail.qualityNotes || control.notes]],
    signatures: [
      { label: 'Responsable', name: responsibleSignature?.signedByName || row.responsible, image: signatureDataUrl, signedAt: responsibleSignature?.createdAt },
      { label: 'Revisado', name: reviewerSignature?.signedByName || control.reviewedBy?.displayName || row.reviewer, image: reviewerSignature?.signatureData?.dataUrl, signedAt: reviewerSignature?.createdAt },
    ],
  })

  printWindow.document.open()
  printWindow.document.write(html)
  printWindow.document.close()
  return true
}

const isToday = (row) => {
  const [datePart] = String(row.date || '').split(',')
  if (!datePart) return false
  return datePart.trim() === new Date().toLocaleDateString('es-ES')
}

export const QualityHistory = () => {
  const isPhone = useMediaQuery('(max-width:599.95px)')
  const { session, status: sessionStatus } = useSuiteSession()
  const currentUser = getUsuarioFromSession(session)
  const hasQualityBackendAccess = Boolean(getApplicationAccess(session, APPLICATION_CODES.qualityProduction))
  const { canWrite, canReview } = getQualityCapabilities(session)
  const [rows, setRows] = React.useState([])
  const [reviewControl, setReviewControl] = React.useState(null)
  const [reviewNotes, setReviewNotes] = React.useState('')
  const [reviewResult, setReviewResult] = React.useState('OK')
  const [reviewBusy, setReviewBusy] = React.useState(false)
  const [reviewError, setReviewError] = React.useState('')
  const [reviewLoaded, setReviewLoaded] = React.useState(false)
  const [reviewDetailsLoaded, setReviewDetailsLoaded] = React.useState(false)
  const [reviewSignatureOpen, setReviewSignatureOpen] = React.useState(false)
  const [reviewSignatureError, setReviewSignatureError] = React.useState('')
  const canDecide = canReview && reviewLoaded && reviewControl && !reviewControl.status?.terminal && ['PENDING_REVIEW', 'WITH_INCIDENT'].includes(reviewControl.statusCode)
  const initialReviewResult = ['OK', 'INCIDENT'].includes(reviewControl?.result) ? reviewControl.result : 'OK'
  const confirmDiscardReview = useUnsavedChanges(Boolean(canDecide && (reviewNotes.trim() || reviewResult !== initialReviewResult)) || reviewBusy)
  const closeReview = () => { if (!reviewBusy && confirmDiscardReview()) setReviewControl(null) }
  const [page, setPage] = React.useState(0)
  const [total, setTotal] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [message, setMessage] = React.useState(null)
  const [filters, setFilters] = React.useState({
    search: '',
    warehouse: 'todos',
    status: 'todos',
    createdFrom: '',
    createdTo: '',
  })
  const invalidDateRange = Boolean(filters.createdFrom && filters.createdTo && filters.createdFrom > filters.createdTo)
  const [queryFilters, setQueryFilters] = React.useState(filters)
  React.useEffect(() => {
    if (invalidDateRange) return undefined
    const timer = setTimeout(() => { setQueryFilters(filters); setPage(0) }, 300)
    return () => clearTimeout(timer)
  }, [filters, invalidDateRange])

  React.useEffect(() => {
    if (sessionStatus !== 'authenticated' || !hasQualityBackendAccess) return undefined
    if (invalidDateRange) { setLoading(false); return undefined }

    let active = true
    setLoading(true)
    getQualityControls({
      page: page + 1, limit: 25, search: queryFilters.search,
      warehouseCode: queryFilters.warehouse === 'todos' ? undefined : queryFilters.warehouse,
      status: queryFilters.status === 'todos' ? undefined : Object.keys(backendStatusLabel).find((key) => backendStatusLabel[key] === queryFilters.status),
      createdFrom: queryFilters.createdFrom || undefined,
      createdTo: queryFilters.createdTo || undefined,
    })
      .then((response) => {
        if (!active) return
        const controls = responseList(response).map(normalizeControl)
        setRows(controls)
        setTotal(response?.meta?.total ?? controls.length)
      })
      .catch((error) => {
        if (active) {
          setRows([])
          setTotal(0)
          setMessage({ severity: 'error', text: apiMessage(error, 'No se ha podido cargar el historico del backend.') })
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [hasQualityBackendAccess, sessionStatus, page, queryFilters, invalidDateRange])

  const filteredRows = rows.filter((row) => {
    const search = filters.search.trim().toLowerCase()
    const matchesSearch = !search || [
      row.id,
      row.order,
      row.client,
      row.variety,
      row.responsible,
    ].join(' ').toLowerCase().includes(search)
    const matchesWarehouse = filters.warehouse === 'todos' || String(row.control?.warehouseCode) === String(filters.warehouse)
    const matchesStatus = filters.status === 'todos' || row.status === filters.status

    return matchesSearch && matchesWarehouse && matchesStatus
  })

  const handleFilterChange = (event) => {
    const { name, value } = event.target
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }))
  }

  const resetFilters = () => {
    setFilters({
      search: '',
      warehouse: 'todos',
      status: 'todos',
      createdFrom: '',
      createdTo: '',
    })
  }

  const loadControlImages = async (control) => ({
    ...control,
    photos: await Promise.all((control.photos || []).map(async (photo) => ({ ...photo, previewUrl: photo.fileUuid ? await getFileDataUrl(photo.fileUuid) : '' }))),
  })

  const handleGeneratePdf = async (row) => {
    const openedWindow = window.open('', '_blank')
    if (!openedWindow) {
      setMessage({ severity: 'error', text: 'No se ha podido abrir la ventana para generar el PDF.' })
      return
    }
    try {
      const control = await loadControlImages(await getQualityControl(row.backendId))
      generateControlPdf({ ...row, control }, openedWindow)
    } catch (error) {
      openedWindow.close()
      setMessage({ severity: 'error', text: apiMessage(error, 'No se ha podido generar el PDF.') })
    }
  }

  const handleOpenReview = async (row) => {
    if (!confirmDiscardReview()) return
    setReviewSignatureOpen(false)
    setReviewSignatureError('')
    setReviewBusy(true)
    setReviewLoaded(false)
    setReviewDetailsLoaded(false)
    setReviewError('')
    setReviewControl(row.control)
    setReviewNotes('')
    try {
      const control = await getQualityControl(row.backendId)
      setReviewControl(control)
      setReviewDetailsLoaded(true)
      setReviewResult(['OK', 'INCIDENT'].includes(control.result) ? control.result : 'OK')
      // Image failures must not hide editing after authoritative details loaded.
      // Full review stays unavailable until all evidence has been loaded.
      setReviewControl(await loadControlImages(control))
      setReviewLoaded(true)
    } catch (error) {
      setReviewError(apiMessage(error, 'No se ha podido abrir el control.'))
    } finally { setReviewBusy(false) }
  }

  const handleReview = async (statusCode, reviewerSignature) => {
    if (!canDecide || reviewBusy || reviewError) return
    if (statusCode === 'COMPLETED' && !reviewerSignature) {
      setReviewSignatureError('')
      setReviewSignatureOpen(true)
      return
    }
    if (statusCode === 'WITH_INCIDENT' && !reviewNotes.trim()) {
      setReviewError('Indica las observaciones para devolver el control.')
      return
    }
    setReviewBusy(true)
    setReviewSignatureError('')
    try {
      const saved = await updateQualityControlStatus(reviewControl.id, {
        statusCode, result: statusCode === 'WITH_INCIDENT' ? 'INCIDENT' : reviewResult, notes: reviewNotes.trim() || undefined,
        ...(statusCode === 'COMPLETED' ? { reviewerSignature } : {}),
      })
      setRows((current) => current.map((row) => row.backendId === saved.id ? normalizeControl(saved) : row))
      setReviewControl(null)
      setReviewSignatureOpen(false)
      setMessage({ severity: 'success', text: statusCode === 'COMPLETED' ? 'Control validado.' : 'Control devuelto con observaciones.' })
    } catch (error) {
      const message = apiMessage(error, 'No se ha podido revisar el control.')
      if (statusCode === 'COMPLETED') setReviewSignatureError(message)
      else setReviewError(message)
    }
    finally { setReviewBusy(false) }
  }

  const todayRows = rows.filter(isToday).length

  const renderRowActions = (row) => <>
    <Button size="small" variant="outlined" startIcon={<VisibilityRoundedIcon />} disabled={reviewBusy} onClick={() => handleOpenReview(row)}>
      {canReview ? 'Revisar' : 'Consultar'}
    </Button>
    <Button size="small" variant="outlined" startIcon={<PictureAsPdfRoundedIcon />} onClick={() => handleGeneratePdf(row)}>PDF</Button>
    {canWrite && ['COMPLETED', 'PENDING_REVIEW'].includes(row.control.statusCode) ? <Button size="small" variant="outlined" component={NavLink} to={`/controles/${row.backendId}`} disabled={reviewBusy}>Editar</Button> : null}
  </>

  return (
    <Box className="page-fade quality-history-page">
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography variant="h4">Historial de partes</Typography>
          <Typography>Consulta controles de calidad realizados, revisiones, incidencias y documentos generados.</Typography>
        </Box>
        {canWrite ? <Button component={NavLink} to="/" variant="contained" startIcon={<FactCheckRoundedIcon />}>Nuevo control</Button> : null}
      </Box>

      <Grid container spacing={2.1} className="quality-history-metrics">
        <Grid size={{ xs: 4, sm: 12, md: 4 }}>
          <AnimatedSection>
            <Card className="quality-history-summary-card">
              <CardContent>
                <Box className="quality-section-icon"><FactCheckRoundedIcon /></Box>
                <Box>
                  <Typography>{total}</Typography>
                  <Typography>Partes registrados</Typography>
                </Box>
              </CardContent>
            </Card>
          </AnimatedSection>
        </Grid>
        <Grid size={{ xs: 4, sm: 12, md: 4 }}>
          <AnimatedSection>
            <Card className="quality-history-summary-card">
              <CardContent>
                <Box className="quality-section-icon"><CalendarMonthRoundedIcon /></Box>
                <Box>
                  <Typography>{todayRows}</Typography>
                  <Typography>Controles de hoy en esta pagina</Typography>
                </Box>
              </CardContent>
            </Card>
          </AnimatedSection>
        </Grid>
        <Grid size={{ xs: 4, sm: 12, md: 4 }}>
          <AnimatedSection>
            <Card className="quality-history-summary-card">
              <CardContent>
                <Box className="quality-section-icon"><VisibilityRoundedIcon /></Box>
                <Box>
                  <Typography>{filteredRows.length}</Typography>
                  <Typography>Resultados visibles</Typography>
                </Box>
              </CardContent>
            </Card>
          </AnimatedSection>
        </Grid>
      </Grid>

      {message && (
        <Alert severity={message.severity} sx={{ mt: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mt: 2 }} />}

      <AnimatedSection>
        <Card className="dashboard-section-card admin-panel-card admin-page-section">
          <CardContent>
            <Box className="admin-users-list-header">
              <Box>
                <Typography className="section-title">Partes de control</Typography>
                <Typography className="quality-section-copy">Listado de controles guardados para consulta y auditoria.</Typography>
              </Box>
              <Chip className="suite-status-chip" label={`${filteredRows.length} partes`} />
            </Box>

            <Box className="quality-history-filter-bar">
              <TextField
                label="Buscar parte"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                size="small"
                className="quality-search-input"
                slotProps={{
                  input: { startAdornment: <SearchRoundedIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} /> },
                }}
              />
              <FormControl size="small" className="quality-filter">
                <InputLabel>Almacen</InputLabel>
                <Select name="warehouse" value={filters.warehouse} label="Almacen" onChange={handleFilterChange}>
                  <MenuItem value="todos">Todos</MenuItem>
                  {[...new Map(rows.filter((row) => row.control?.warehouseCode && !['0', '4'].includes(String(row.control.warehouseCode).trim())).map((row) => [row.control.warehouseCode, row.control.warehouseName])).entries()].map(([code, name]) => <MenuItem key={code} value={code}>{getWarehouseLabel(code, name)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" className="quality-filter">
                <InputLabel>Estado</InputLabel>
                <Select name="status" value={filters.status} label="Estado" onChange={handleFilterChange}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="Borrador">Borrador</MenuItem>
                  <MenuItem value="Completado">Completado</MenuItem>
                  <MenuItem value="Pendiente revision">Pendiente revision</MenuItem>
                  <MenuItem value="Con incidencia">Con incidencia</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="Fecha desde"
                name="createdFrom"
                value={filters.createdFrom}
                onChange={handleFilterChange}
                size="small"
                helperText="Fecha de registro"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: filters.createdTo || undefined } }}
              />
              <TextField
                type="date"
                label="Fecha hasta"
                name="createdTo"
                value={filters.createdTo}
                onChange={handleFilterChange}
                size="small"
                error={invalidDateRange}
                helperText={invalidDateRange ? 'La fecha final no puede ser anterior a la inicial.' : 'Fecha de registro'}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: filters.createdFrom || undefined } }}
              />
              <Button variant="outlined" startIcon={<CleaningServicesRoundedIcon />} onClick={resetFilters}>
                Limpiar filtros
              </Button>
            </Box>

            {isPhone ? <Box className="quality-history-mobile-list" aria-label="Partes de control">
              {filteredRows.map((row) => <Box component="article" className="quality-history-mobile-card" key={row.id}>
                <Box className="quality-history-mobile-card-heading">
                  <Typography className="quality-history-id">{row.id}</Typography>
                  <Chip size="small" className={`admin-status-chip ${statusClass[row.status]}`} label={row.status} />
                </Box>
                <Typography className="quality-history-mobile-order">{row.order} · Linea {row.line}</Typography>
                <Typography>{row.client}</Typography>
                <Box component="dl" className="quality-history-mobile-details">
                  {[
                    ['Almacen', row.warehouse], ['Fecha', row.date],
                    ['Variedad', row.variety], ['Momento', row.moment],
                    ['Responsable', row.responsible], ['Revisor', row.reviewer],
                  ].map(([label, value]) => <Box key={label}><Typography component="dt">{label}</Typography><Typography component="dd">{value || '-'}</Typography></Box>)}
                </Box>
                <Box className="quality-history-mobile-actions">{renderRowActions(row)}</Box>
              </Box>)}
            </Box> : <Box className="activity-table-wrap quality-history-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parte</TableCell>
                    <TableCell>Pedido</TableCell>
                    <TableCell>Almacen</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Variedad</TableCell>
                    <TableCell>Momento</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Responsable</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row) => (
                    <TableRow component={AnimatedTableRow} key={row.id} hover className="admin-clickable-row">
                      <TableCell>
                        <Typography className="quality-history-id">{row.id}</Typography>
                        <Typography className="admin-table-subtext">Linea {row.line}</Typography>
                      </TableCell>
                      <TableCell>{row.order}</TableCell>
                      <TableCell>{row.warehouse}</TableCell>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.client}</TableCell>
                      <TableCell>{row.variety}</TableCell>
                      <TableCell>{row.moment}</TableCell>
                      <TableCell>
                        <Chip size="small" className={`admin-status-chip ${statusClass[row.status]}`} label={row.status} />
                      </TableCell>
                      <TableCell>
                        <Typography>{row.responsible}</Typography>
                        <Typography className="admin-table-subtext">Rev. {row.reviewer}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box className="quality-history-row-actions">{renderRowActions(row)}</Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>}
            {!rows.length && !loading ? <Typography>No hay controles para los filtros indicados.</Typography> : null}
            <TablePagination className="quality-orders-pagination" component="div" count={total} page={page} rowsPerPage={25} rowsPerPageOptions={[25]}
              onPageChange={(_, nextPage) => setPage(nextPage)} labelRowsPerPage="Filas por pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              getItemAriaLabel={(type) => ({ next: 'Pagina siguiente', previous: 'Pagina anterior' }[type] || 'Pagina')} />
          </CardContent>
        </Card>
      </AnimatedSection>
      <Dialog open={Boolean(reviewControl)} onClose={closeReview} fullWidth maxWidth="md" fullScreen={isPhone} className="quality-mobile-dialog quality-review-dialog" aria-labelledby="quality-review-title">
        <DialogTitle id="quality-review-title">{reviewControl?.controlNo} - {canReview ? 'Revision del control' : 'Consulta del control'}</DialogTitle>
        <DialogContent>
          {reviewBusy ? <LinearProgress /> : null}
          {reviewError ? <Alert severity="error" onClose={() => setReviewError('')}>{reviewError}</Alert> : null}
          {reviewControl ? <Stack spacing={2}>
            <Typography>Pedido {reviewControl.orderNo} / Linea {reviewControl.lineNo} | {reviewControl.customerName}</Typography>
            <Typography>Estado: {reviewControl.status?.label || reviewControl.statusCode} | Responsable: {reviewControl.responsibleUser?.displayName || reviewControl.createdBy?.displayName || '-'}</Typography>
            <Box className="quality-readonly-grid">
              {[
                ['Variedad', reviewControl.varietyName], ['Confeccion', reviewControl.confectionName],
                ['Palets', reviewControl.detail?.orderedPallets], ['Bultos', reviewControl.detail?.packages],
                ['Envase', reviewControl.detail?.boxType], ['Palet', reviewControl.detail?.palletType],
                ['Calibre', reviewControl.detail?.caliber], ['Calibre real', reviewControl.detail?.realCaliber],
                ['Producto certificado', certifiedProductLabel[reviewControl.detail?.certifiedProduct]],
                ['Banda', reviewControl.detail?.band], ['Total ampliacion', reviewControl.detail?.totalExtension],
                ['Variedad real', reviewControl.detail?.realVariety], ['Peso minimo', reviewControl.detail?.minWeight],
                ['Notas de linea', reviewControl.detail?.lineNotes], ['Observaciones de calidad', reviewControl.detail?.qualityNotes || reviewControl.notes],
              ].map(([label, value]) => <Box key={label}><Typography>{label}</Typography><Typography>{value || '-'}</Typography></Box>)}
            </Box>
            {(reviewControl.checks || []).map((check) => <Typography key={check.id || check.code}>{check.label}: {okNoOkLabel(check.value)} {check.notes || ''}</Typography>)}
            <Box className="quality-photo-grid">{(reviewControl.photos || []).filter((photo) => photo.previewUrl).map((photo) => <Box key={photo.id || photo.fileId}><Typography>{photo.label || photo.photoType}</Typography><Box component="img" src={photo.previewUrl} alt={photo.label || photo.photoType} sx={{ width: '100%', height: 200, objectFit: 'contain' }} /></Box>)}</Box>
            {(reviewControl.signatures || []).filter((signature) => signature.signatureData?.dataUrl).map((signature) => <Box key={signature.id || signature.signatureType}><Typography>{signature.signatureType === 'REVIEWER' ? 'Firma del revisor' : 'Firma del responsable'}: {signature.signedByName}</Typography>{signature.createdAt ? <Typography>{formatDateTime(signature.createdAt)}</Typography> : null}<Box component="img" src={signature.signatureData.dataUrl} alt={signature.signatureType === 'REVIEWER' ? 'Firma del revisor' : 'Firma del responsable'} sx={{ width: '100%', height: 90, objectFit: 'contain' }} /></Box>)}
            <Typography>Ultima revision: {reviewControl.reviewedBy?.displayName || '-'} | {reviewControl.reviewNotes || '-'}</Typography>
            <Stack spacing={1} component="section" aria-label="Historial de cambios">
              <Typography fontWeight={800}>Historial de cambios</Typography>
              {(reviewControl.events || []).map((event) => <Box key={event.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, overflowWrap: 'anywhere' }}>
                <Typography fontWeight={700}>{formatDateTime(event.createdAt)} | {qualityEventLabel(event)} | {event.createdBy?.displayName || (event.createdByUserId ? `Usuario ${event.createdByUserId}` : 'Autor no registrado')}</Typography>
                {event.previousStatus || event.newStatus ? <Typography variant="body2">{backendStatusLabel[event.previousStatus] || event.previousStatus || '-'} → {backendStatusLabel[event.newStatus] || event.newStatus || '-'}</Typography> : null}
                {event.notes ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{event.notes}</Typography> : null}
                {qualityControlChanges(event).map((change) => <Box key={change.label} sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={700}>{change.label}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>Antes: {change.before}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>Despues: {change.after}</Typography>
                </Box>)}
              </Box>)}
              {!reviewControl.events?.length ? <Typography variant="body2">No hay cambios registrados.</Typography> : null}
            </Stack>
            {canDecide ? <>
              <FormControl size="small"><InputLabel>Resultado</InputLabel><Select label="Resultado" value={reviewResult} onChange={(event) => setReviewResult(event.target.value)}><MenuItem value="OK">Conforme</MenuItem><MenuItem value="INCIDENT">Con incidencia</MenuItem></Select></FormControl>
              <TextField label="Observaciones de revision" value={reviewNotes} onChange={(event) => { setReviewNotes(event.target.value); setReviewError('') }} multiline minRows={3} />
            </> : null}
          </Stack> : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={reviewBusy} onClick={closeReview}>Cerrar</Button>
          {canWrite && reviewDetailsLoaded && ['COMPLETED', 'PENDING_REVIEW'].includes(reviewControl?.statusCode) ? <Button component={NavLink} to={`/controles/${reviewControl.id}`} disabled={reviewBusy} variant="outlined">Editar control</Button> : null}
          {canDecide ? <><Button color="warning" disabled={reviewBusy || Boolean(reviewError)} onClick={() => handleReview('WITH_INCIDENT')}>Devolver con observaciones</Button><Button variant="contained" disabled={reviewBusy || Boolean(reviewError)} onClick={() => handleReview('COMPLETED')}>Validar control</Button></> : null}
        </DialogActions>
      </Dialog>
      <SignatureDialog
        open={reviewSignatureOpen}
        title="Firma del revisor"
        signerName={currentUser?.displayName || currentUser?.username || 'Usuario revisor'}
        confirmLabel="Firmar y validar control"
        busy={reviewBusy}
        error={reviewSignatureError}
        autoClose={false}
        onClose={() => { if (!reviewBusy) { setReviewSignatureOpen(false); setReviewSignatureError('') } }}
        onSave={(signature) => handleReview('COMPLETED', signature)}
      />
    </Box>
  )
}
